import { Directive, HostListener, InjectionToken, Type } from '@angular/core'
import { ComponentContainer, JsonValue } from 'golden-layout'
import { PanelMetadata } from './panel-metadata'

/**
 * Definition for a panel that gets registered with the layout.
 */
export interface PanelConfig extends PanelMetadata {
  /**
   * Angular component type.
   * Pass the class of the panel component that will be rendered in the layout.
   */
  panelComponent: Type<BasePanelComponent>
}

/**
 * Injection token so that all panels can pass the ComponentContainer to the superclass, BasePanelComponent
 * All panels *must* have this injected in their constructor like this:
 * ```
 * constructor(
 *
 *   @Inject(LayoutContainer) container: ComponentContainer,
 *   ...and any other dependecies, such as Store<RootState>...
 * ) {
 *   super(container)
 * }
 * ```
 *
 * _note_: in test code, MockComponentContainer is used, so that unit tests never have to mess with GoldenLayout
 */
export const LayoutContainer = new InjectionToken<ComponentContainer>(
  'GoldenLayoutContainer',
)

export const borderlessPanelClass = 'hl_borderless_panel'

/**
 * All panels should extend this abstract class. This class
 * provides handling for panel state and also creates a
 * contract for using panels in other areas of the layout.
 *
 * @typedef T - The interface for the state object of the panel
 */
@Directive({
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    role: 'dialog',
  },
})
export abstract class BasePanelComponent<T extends JsonValue = JsonValue> {
  /**
   * The current state of the panel.
   */
  state?: T
  /**
   * The initial state of the panel when it was created.
   */
  initialState?: T

  private _title?: string

  /**
   * Create helios panel
   *
   * @param container This needs to be injected from angular. `@Inject(LayoutContainer) container: ComponentContainer`
   */
  constructor(protected readonly container: ComponentContainer) {
    container.stateRequestEvent = () => this.handleContainerStateRequestEvent()

    this.initialState = container.initialState as T
    this.state = this.initialState
  }

  /**
   * GoldenLayout will save the current state to the state config.
   * If a panel has state this state will be stored in the config.
   * Anything that the panel requires to load correctly from a saved
   * state should be stored in this state object.
   *
   * @returns current state of the panel
   */
  private handleContainerStateRequestEvent(): T | undefined {
    return this.state != null ? this.state : undefined
  }

  @HostListener('click')
  private setPanelFocused() {
    this.focusPanel()
  }

  /**
   * Sets a tooltip for the tab's title, including a relevant icon
   *
   * @internal for testing
   * @param message to the user, or undefined to remove the tooltip
   * @param severity ERROR/WARNING for the appropirate icon, or use undefined when message is undefined
   */
  setTitleTooltip(message: string | undefined, severity?: 'ERROR' | 'WARNING') {
    // Break early if it's not a tabbed component
    if (!this.container.tab) {
      return
    }

    const title = this.getTitle()

    this.container.tab.element.classList.remove('warning-title')
    this.container.tab.element.classList.remove('errored-title')

    if (!message) {
      //I am not sure why tab.setTitle does not update titleElement.title, but yes both lines are needed.
      this.container.tab.setTitle(title)
      this.container.tab.titleElement.title = title
      return
    }

    if (!severity) throw new Error('expected severity when adding a tooltip')

    if (severity === 'ERROR') {
      this.container.tab.titleElement.innerHTML = `<span class="mdi mdi-alert-circle text-danger"></span>${title}`
      this.container.tab.titleElement.title = message
      this.container.tab.element.classList.add('errored-title')
      return
    }

    if (severity === 'WARNING') {
      this.container.tab.titleElement.innerHTML = `<span class="mdi mdi-alert text-warning"></span>${title}`
      this.container.tab.titleElement.title = message
      this.container.tab.element.classList.add('warning-title')
      return
    }
  }

  /**
   * title is set by layout manager, in the ComponentItemConfig, which sets it in the container's tab.titleElement
   * We keep a local this._title as a cache of the original value as it gets modified with tooltips and such
   * @internal for testing */
  private getTitle(): string {
    if (this._title != null) return this._title

    // The tab doesn't exist yet
    if (!this.container.tab) {
      return ''
    }

    if (this.container.tab.titleElement.innerText == null) {
      /**
       * The tab not having a title should not happen.
       * If we notice these errors in the future I would first try to move the original call from the ngOnInit
       * life cycle hook to the ngAfterViewInit hook. If that does not work, we may have to do something fancy like
       * listen to the inner GL layout managers (container.layoutManager) state change events.
       */
      throw Error(`No tab title set for panel: ${this.getPanelName()}`)
    } else {
      this._title = this.container.tab.titleElement.innerText
      return this._title
    }
  }

  private getPanelName(): string {
    return this.container.componentType as string
  }

  protected focusPanel() {
    this.container.focus()
  }

  protected setState(newState: T) {
    this.state = newState
    this.container.layoutManager.emit('stateChanged')
  }
}
